import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// jsPDF's built-in fonts (helvetica/times/courier) use WinAnsi encoding and
// don't include ₹ (U+20B9) or ≤ (U+2264). Use ASCII-safe substitutions.
const RS = "Rs ";
const LTE = "<=";

const fmtL = (n) => (n == null ? "-" : `${(Number(n) / 1e5).toFixed(2)} L`);
const fmtPctSigned = (n) =>
  n == null || isNaN(n) ? "-" : `${n >= 0 ? "+" : ""}${Number(n).toFixed(1)}%`;
const todayLong = () =>
  new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export const exportLeaderboardPDF = ({ scopeLabel, kpis, rows, brandName = "Retail Intelligence Demo" }) => {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Header ribbon
  doc.setFillColor(20, 20, 43);
  doc.rect(0, 0, pageW, 6, "F");

  // Brand
  doc.setFont("times", "bold");
  doc.setFontSize(22);
  doc.setTextColor(20, 20, 43);
  doc.text("Angadya", 40, 55);

  doc.setFont("times", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 107, 123);
  doc.text(`${brandName} - Multi-branch health snapshot`, 40, 74);
  doc.text(todayLong(), pageW - 40, 74, { align: "right" });

  doc.setDrawColor(231, 228, 220);
  doc.setLineWidth(1);
  doc.line(40, 90, pageW - 40, 90);

  doc.setFontSize(9);
  doc.setTextColor(20, 20, 43);
  doc.text(`SCOPE   ${(scopeLabel || "All branches").replace(/·/g, "-")}`, 40, 108);

  // KPI strip
  const kpiY = 130;
  const cellW = (pageW - 80) / 4;
  const kpiItems = [
    { label: "Today's Revenue", value: `${RS}${fmtL(kpis.todays_revenue)}`,
      sub: `${fmtPctSigned(kpis.todays_revenue_delta_pct)} vs. yesterday` },
    { label: "Avg. Health Score", value: `${kpis.avg_health_score}`,
      sub: "composite - 0 to 100" },
    { label: "Needs Attention", value: `${kpis.branches_needing_attention} / ${kpis.total_branches}`,
      sub: "amber or red band" },
    { label: "Wastage Risk", value: `${RS}${fmtL(kpis.wastage_risk_value)}`,
      sub: `${LTE} 1 day to expiry` },
  ];
  kpiItems.forEach((k, i) => {
    const x = 40 + cellW * i;
    doc.setDrawColor(231, 228, 220);
    doc.setLineWidth(0.5);
    doc.line(x, kpiY, x, kpiY + 50);
    doc.setFontSize(7.5);
    doc.setTextColor(107, 107, 123);
    doc.text(k.label.toUpperCase(), x + 8, kpiY + 12);
    doc.setFont("times", "bold");
    doc.setFontSize(14);
    doc.setTextColor(20, 20, 43);
    doc.text(k.value, x + 8, kpiY + 32);
    doc.setFont("times", "normal");
    doc.setFontSize(8);
    doc.setTextColor(107, 107, 123);
    doc.text(k.sub, x + 8, kpiY + 46);
  });
  doc.line(40 + cellW * 4, kpiY, 40 + cellW * 4, kpiY + 50);
  doc.line(40, kpiY + 50, pageW - 40, kpiY + 50);

  // Section header
  doc.setFont("times", "bold");
  doc.setFontSize(13);
  doc.setTextColor(20, 20, 43);
  doc.text("Branches ranked by health", 40, 205);
  doc.setFont("times", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 107, 123);
  doc.text("sorted high to low - read-only snapshot", 40, 218);

  // Table
  autoTable(doc, {
    startY: 230,
    head: [["Rank", "Branch", "Region", "Health", "Band", `Today (${RS}L)`, "Wk/Wk", "Flags"]],
    body: rows.map((r) => [
      r.rank,
      r.branch_name,
      `${r.region} Chennai`,
      r.health_score,
      r.health_band.toUpperCase(),
      (r.todays_revenue / 1e5).toFixed(2),
      fmtPctSigned(r.wow_delta_pct),
      [
        r.wastage_flag && "Wastage",
        r.stockout_flag && "Stock-out",
        r.staffing_flag && "Staffing",
      ].filter(Boolean).join(", ") || "-",
    ]),
    styles: { font: "helvetica", fontSize: 9, cellPadding: 5, lineColor: [231, 228, 220] },
    headStyles: {
      fillColor: [20, 20, 43], textColor: [255, 255, 255],
      fontStyle: "bold", halign: "left", fontSize: 8,
    },
    alternateRowStyles: { fillColor: [250, 249, 246] },
    columnStyles: {
      0: { halign: "right", cellWidth: 34 },
      3: { halign: "right", cellWidth: 40 },
      4: { cellWidth: 44 },
      5: { halign: "right", cellWidth: 62 },
      6: { halign: "right", cellWidth: 50 },
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 4) {
        const band = String(data.cell.raw || "").toLowerCase();
        if (band === "green") data.cell.styles.textColor = [47, 139, 94];
        else if (band === "amber") data.cell.styles.textColor = [184, 132, 42];
        else if (band === "red") data.cell.styles.textColor = [183, 64, 42];
        data.cell.styles.fontStyle = "bold";
      }
      if (data.section === "body" && data.column.index === 6) {
        const raw = String(data.cell.raw || "");
        if (raw.startsWith("+")) data.cell.styles.textColor = [47, 139, 94];
        else if (raw.startsWith("-")) data.cell.styles.textColor = [183, 64, 42];
      }
    },
    margin: { left: 40, right: 40 },
  });

  // Footer on every page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(107, 107, 123);
    doc.text("Angadya - operational snapshot", 40, pageH - 24);
    doc.text(`Page ${i} of ${pageCount}`, pageW - 40, pageH - 24, { align: "right" });
    doc.setFillColor(201, 161, 92);
    doc.rect(40, pageH - 18, 16, 2, "F");
  }

  doc.save(`angadya-leaderboard-${new Date().toISOString().slice(0, 10)}.pdf`);
};
